/**
 * Quick View Modal JavaScript - REVISED VERSION
 * Improvements:
 * - Fixed image rendering bug
 * - Added proper money formatting
 * - Enhanced error handling
 * - Added caching mechanism
 * - Improved accessibility
 * - Fixed XSS vulnerabilities
 * - Custom description support
 */

class QuickViewModal {
  constructor() {
    this.modal = document.getElementById('quickViewModal');
    this.content = this.modal?.querySelector('.quick-view-content');
    this.loading = document.getElementById('quickViewLoading');
    this.currentProduct = null;
    this.currentVariant = null;
    this.selectedOptions = {};
    this.productCache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.triggerButton = null;
    
    if (this.modal && this.content && this.loading) {
      this.init();
    } else {
      console.error('QuickView: Required elements not found in DOM');
    }
  }

  init() {
    // Add event listeners to all quick view buttons
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.quick-view-button');
      if (button) {
        e.preventDefault();
        this.triggerButton = button;
        
        // Support both data-product-url and data-product-handle
        let productUrl = button.getAttribute('data-product-url');
        
        if (!productUrl) {
          const handle = button.getAttribute('data-product-handle');
          if (handle) {
            productUrl = `/products/${handle}`;
          }
        }
        
        if (productUrl) {
          this.openModal(productUrl);
        }
      }
    });

    // Close modal events
    const closeElements = this.modal.querySelectorAll('[data-close-modal]');
    closeElements.forEach(el => {
      el.addEventListener('click', () => this.closeModal());
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });

    // Prevent modal close when clicking inside content
    const container = this.modal.querySelector('.quick-view-container');
    if (container) {
      container.addEventListener('click', (e) => e.stopPropagation());
    }

    // Form submit handler
    const form = document.getElementById('quickViewForm');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Quantity buttons
    this.setupQuantityButtons();
  }

  async openModal(productUrl) {
    // Show modal with loading state
    this.modal.classList.add('active');
    this.content.style.display = 'none';
    this.loading.style.display = 'block';
    document.body.classList.add('quick-view-open');

    try {
      // Check cache first
      const cached = this.productCache.get(productUrl);
      if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {
        this.currentProduct = cached.data;
        this.currentVariant = cached.data.variants[0];
        this.populateModal(cached.data);
        this.showContent();
        return;
      }

      // Fetch product data with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${productUrl}.js`, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const product = await response.json();
      
      // Validate product data
      if (!product || !product.variants || product.variants.length === 0) {
        throw new Error('Invalid product data received');
      }

      // Cache the product
      this.productCache.set(productUrl, {
        data: product,
        timestamp: Date.now()
      });

      this.currentProduct = product;
      this.currentVariant = product.variants[0];
      this.selectedOptions = {};
      
      // Populate modal
      this.populateModal(product);
      this.showContent();
      
    } catch (error) {
      console.error('Error loading product:', error);
      
      let errorMessage = 'Failed to load product details. Please try again.';
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection.';
      }
      
      this.showError(errorMessage);
    }
  }

  showContent() {
    this.loading.style.display = 'none';
    this.content.style.display = 'block';
    this.trapFocus();
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.classList.remove('quick-view-open');
    
    // Return focus to trigger button
    if (this.triggerButton) {
      this.triggerButton.focus();
      this.triggerButton = null;
    }
    
    // Reset content after animation
    setTimeout(() => {
      this.content.style.display = 'none';
      this.loading.style.display = 'block';
      this.hideSuccess();
    }, 300);
  }

  populateModal(product) {
    // Title
    const titleEl = document.getElementById('quickViewTitle');
    if (titleEl) titleEl.textContent = product.title || 'Untitled Product';
    
    // Vendor
    const vendorEl = document.getElementById('quickViewVendor');
    if (vendorEl) {
      if (product.vendor) {
        vendorEl.textContent = product.vendor;
        vendorEl.style.display = 'block';
      } else {
        vendorEl.style.display = 'none';
      }
    }

    // Price
    this.updatePrice(this.currentVariant);

    // Images
    this.populateImages(product);

    // Description with custom text support
    this.populateDescription(product);

    // Badges
    this.populateBadges(product);

    // Variants
    this.populateVariants(product);

    // Stock status
    this.updateStock(this.currentVariant);

    // Full details link
    const detailsLink = document.getElementById('quickViewFullDetails');
    if (detailsLink) {
      detailsLink.href = product.url || '#';
    }

    // Rating
    this.populateRating(product);
  }

  populateDescription(product) {
    const descEl = document.getElementById('quickViewDescription');
    if (!descEl) return;

    // Custom descriptions for specific products (HTML allowed for bold text)
    const customDescriptions = {
      'pumpums': '<strong>Natural Period Cramp Relief Gummies</strong> — Sugar-Free, Plant-Based, and Bloat-Busting. Say goodbye to monthly discomfort with <strong>PumPums Period Cramp Relief</strong> — your clean, plant-powered solution for easing <strong>cramps</strong>, <strong>bloating</strong>, and supporting <strong>gut health</strong> during your cycle.',
      // Add more custom descriptions here:
      // 'product-handle': 'Custom description with <strong>bold text</strong>...',
    };

    // Check if product has a custom description
    let customDesc = null;
    const productHandle = product.handle?.toLowerCase();
    const productTitle = product.title?.toLowerCase();

    // Try to match by handle or title
    for (const [key, desc] of Object.entries(customDescriptions)) {
      if (productHandle?.includes(key) || productTitle?.includes(key)) {
        customDesc = desc;
        break;
      }
    }

    if (customDesc) {
      // Use innerHTML for custom descriptions to allow HTML formatting
      descEl.innerHTML = customDesc;
    } else if (product.description) {
      // Default: Strip HTML and get first few sentences (use textContent for security)
      const text = this.stripHtml(product.description);
      const sentences = text.split(/[.!?]+/).filter(s => s.trim()).slice(0, 3);
      descEl.textContent = sentences.join('. ') + (sentences.length > 0 ? '.' : '');
    } else {
      descEl.textContent = '';
    }
  }

  populateImages(product) {
    const mainImageEl = document.getElementById('quickViewMainImage');
    const thumbnailsContainer = document.getElementById('quickViewThumbnails');
    
    if (!mainImageEl || !thumbnailsContainer) return;

    // Reset thumbnails
    thumbnailsContainer.innerHTML = '';

    // Handle no images
    if (!product.images || product.images.length === 0) {
      mainImageEl.src = 'https://via.placeholder.com/600x600?text=No+Image';
      mainImageEl.alt = this.escapeHtml(product.title);
      mainImageEl.width = 600;
      mainImageEl.height = 600;
      return;
    }

    // Set main image
    mainImageEl.src = product.featured_image || product.images[0];
    mainImageEl.alt = this.escapeHtml(product.title);
    mainImageEl.width = 600;
    mainImageEl.height = 600;

    // Create thumbnails
    product.images.forEach((imgSrc, index) => {
      const thumb = document.createElement('button');
      thumb.className = 'quick-view-thumbnail' + (index === 0 ? ' active' : '');
      thumb.type = 'button';
      thumb.setAttribute('aria-label', `View image ${index + 1}`);
      
      const thumbImg = document.createElement('img');
      thumbImg.src = imgSrc;
      thumbImg.alt = `${this.escapeHtml(product.title)} - Image ${index + 1}`;
      thumbImg.width = 100;
      thumbImg.height = 100;
      thumbImg.loading = index < 3 ? 'eager' : 'lazy';

      thumb.appendChild(thumbImg);
      thumb.addEventListener('click', () => {
        mainImageEl.src = imgSrc;
        document.querySelectorAll('.quick-view-thumbnail').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
      thumbnailsContainer.appendChild(thumb);
    });
  }

  populateVariants(product) {
    const variantsContainer = document.getElementById('quickViewVariants');
    if (!variantsContainer) return;
    
    variantsContainer.innerHTML = '';

    if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
      const variantIdInput = document.getElementById('quickViewVariantId');
      if (variantIdInput) {
        variantIdInput.value = product.variants[0].id;
      }
      return;
    }

    // Group variants by option name
    const options = product.options || [];
    
    options.forEach((option, optionIndex) => {
      if (option.name === 'Title') return;
      
      const variantGroup = document.createElement('div');
      variantGroup.className = 'variant-group';
      
      const label = document.createElement('label');
      label.textContent = option.name;
      label.id = `variant-label-${optionIndex}`;
      variantGroup.appendChild(label);
      
      const optionsContainer = document.createElement('div');
      optionsContainer.className = 'variant-options';
      optionsContainer.setAttribute('role', 'radiogroup');
      optionsContainer.setAttribute('aria-labelledby', `variant-label-${optionIndex}`);
      
      option.values.forEach((value) => {
        const optionButton = document.createElement('button');
        optionButton.type = 'button';
        optionButton.className = 'variant-option';
        optionButton.textContent = value;
        optionButton.dataset.optionName = option.name;
        optionButton.dataset.optionValue = value;
        optionButton.setAttribute('role', 'radio');
        optionButton.setAttribute('aria-checked', 'false');
        
        // Initialize selected options
        if (!this.selectedOptions[option.name]) {
          this.selectedOptions[option.name] = value;
          optionButton.classList.add('active');
          optionButton.setAttribute('aria-checked', 'true');
        } else if (this.selectedOptions[option.name] === value) {
          optionButton.classList.add('active');
          optionButton.setAttribute('aria-checked', 'true');
        }
        
        // Check availability
        const isAvailable = this.checkOptionAvailability(option.name, value, product);
        if (!isAvailable) {
          optionButton.classList.add('unavailable');
          optionButton.disabled = true;
          optionButton.setAttribute('aria-disabled', 'true');
        }
        
        optionButton.addEventListener('click', () => {
          this.selectOption(option.name, value, optionButton, product);
        });
        
        optionsContainer.appendChild(optionButton);
      });
      
      variantGroup.appendChild(optionsContainer);
      variantsContainer.appendChild(variantGroup);
    });

    // Set initial variant
    this.updateVariant(product);
  }

  selectOption(optionName, value, button, product) {
    this.selectedOptions[optionName] = value;
    
    // Update active state and ARIA
    const group = button.closest('.variant-group');
    if (group) {
      group.querySelectorAll('.variant-option').forEach(opt => {
        opt.classList.remove('active');
        opt.setAttribute('aria-checked', 'false');
      });
    }
    button.classList.add('active');
    button.setAttribute('aria-checked', 'true');
    
    // Update variant
    this.updateVariant(product);
  }

  updateVariant(product) {
    // Find matching variant
    const variant = product.variants.find(v => {
      return Object.keys(this.selectedOptions).every((key, index) => {
        return v.options && v.options[index] === this.selectedOptions[key];
      });
    });

    if (variant) {
      this.currentVariant = variant;
      const variantIdInput = document.getElementById('quickViewVariantId');
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
      this.updatePrice(variant);
      this.updateStock(variant);
      
      // Update main image if variant has its own
      if (variant.featured_image && variant.featured_image.src) {
        const mainImg = document.getElementById('quickViewMainImage');
        if (mainImg) {
          mainImg.src = variant.featured_image.src;
        }
      }
    }
  }

  checkOptionAvailability(optionName, value, product) {
    const optionIndex = product.options.findIndex(opt => opt.name === optionName);
    if (optionIndex === -1) return false;
    
    return product.variants.some(variant => {
      return variant.options && variant.options[optionIndex] === value && variant.available;
    });
  }

  updatePrice(variant) {
    const priceContainer = document.getElementById('quickViewPrice');
    if (!priceContainer || !variant) return;
    
    const currentPrice = this.formatMoney(variant.price);
    
    let priceHTML = `<span class="price-current">${currentPrice}</span>`;
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const comparePrice = this.formatMoney(variant.compare_at_price);
      const savings = variant.compare_at_price - variant.price;
      const percentOff = Math.round((savings / variant.compare_at_price) * 100);
      
      priceHTML += `
        <span class="price-compare">${comparePrice}</span>
        <span class="price-save">Save ${percentOff}%</span>
      `;
    }
    
    priceContainer.innerHTML = priceHTML;
  }

  updateStock(variant) {
    const stockEl = document.getElementById('quickViewStock');
    const addButton = document.getElementById('quickViewAddToCart');
    const buttonText = document.getElementById('quickViewButtonText');
    
    if (!stockEl || !addButton || !buttonText || !variant) return;
    
    if (!variant.available) {
      stockEl.innerHTML = `
        <span class="stock-indicator"></span>
        <span>Out of Stock</span>
      `;
      stockEl.className = 'quick-view-stock stock-out-of-stock';
      addButton.disabled = true;
      buttonText.textContent = 'Out of Stock';
    } else {
      const inventory = variant.inventory_quantity;
      
      if (inventory && inventory <= 10 && inventory > 0) {
        stockEl.innerHTML = `
          <span class="stock-indicator"></span>
          <span>Only ${inventory} left in stock</span>
        `;
        stockEl.className = 'quick-view-stock stock-low-stock';
      } else {
        stockEl.innerHTML = `
          <span class="stock-indicator"></span>
          <span>In Stock</span>
        `;
        stockEl.className = 'quick-view-stock stock-in-stock';
      }
      
      addButton.disabled = false;
      buttonText.textContent = 'Add to Cart';
    }
  }

  populateBadges(product) {
    const badgesContainer = document.getElementById('quickViewBadges');
    if (!badgesContainer) return;
    
    badgesContainer.innerHTML = '';
    const badges = [];
    
    // Sale badge
    if (this.currentVariant && this.currentVariant.compare_at_price && 
        this.currentVariant.compare_at_price > this.currentVariant.price) {
      badges.push({ text: '🔥 Sale', class: 'sale' });
    }
    
    // Tag-based badges
    if (product.tags && Array.isArray(product.tags)) {
      const tagBadges = {
        'bestseller': '⭐ Bestseller',
        'new': '✨ New',
        'vegan': '🌱 Vegan',
        'organic': '🍃 Organic',
        'gluten-free': '🌾 Gluten Free',
        'non-gmo': '🧬 Non-GMO'
      };
      
      product.tags.forEach(tag => {
        const normalizedTag = String(tag).toLowerCase().replace(/\s+/g, '-');
        if (tagBadges[normalizedTag]) {
          badges.push({ text: tagBadges[normalizedTag], class: normalizedTag });
        }
      });
    }
    
    badges.forEach(badge => {
      const badgeEl = document.createElement('span');
      badgeEl.className = `product-badge ${badge.class}`;
      badgeEl.textContent = badge.text;
      badgesContainer.appendChild(badgeEl);
    });
  }

  populateRating(product) {
    const ratingEl = document.getElementById('quickViewRating');
    if (ratingEl) {
      ratingEl.innerHTML = '';
    }
  }

  setupQuantityButtons() {
    const decreaseBtn = this.modal?.querySelector('.quantity-decrease');
    const increaseBtn = this.modal?.querySelector('.quantity-increase');
    const quantityInput = document.getElementById('quickViewQuantity');
    
    if (!quantityInput) return;
    
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      });
    }
    
    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        const maxQuantity = this.currentVariant?.inventory_quantity || 999;
        if (currentValue < maxQuantity) {
          quantityInput.value = currentValue + 1;
        }
      });
    }
    
    quantityInput.addEventListener('change', () => {
      let value = parseInt(quantityInput.value) || 1;
      if (value < 1) value = 1;
      const maxQuantity = this.currentVariant?.inventory_quantity || 999;
      if (value > maxQuantity) value = maxQuantity;
      quantityInput.value = value;
    });
  }

  async addToCart() {
    const addButton = document.getElementById('quickViewAddToCart');
    const buttonText = document.getElementById('quickViewButtonText');
    
    if (!this.currentVariant || !this.currentVariant.available || !addButton || !buttonText) {
      return;
    }
    
    addButton.classList.add('loading');
    addButton.disabled = true;
    const originalText = buttonText.textContent;
    
    const quantity = parseInt(document.getElementById('quickViewQuantity')?.value) || 1;
    
    const formData = {
      items: [{
        id: this.currentVariant.id,
        quantity: quantity
      }]
    };
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }
      
      await response.json();
      
      this.showSuccess();
      await this.updateCartCount();
      
      addButton.classList.remove('loading');
      addButton.disabled = false;
      
      setTimeout(() => {
        this.closeModal();
      }, 1500);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      addButton.classList.remove('loading');
      addButton.disabled = false;
      buttonText.textContent = 'Error - Try Again';
      
      setTimeout(() => {
        buttonText.textContent = originalText;
      }, 2000);
    }
  }

  showSuccess() {
    const successEl = document.getElementById('quickViewSuccess');
    if (successEl) {
      successEl.style.display = 'flex';
    }
  }

  hideSuccess() {
    const successEl = document.getElementById('quickViewSuccess');
    if (successEl) {
      successEl.style.display = 'none';
    }
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'quick-view-error';
    errorDiv.style.cssText = 'padding:40px;text-align:center;color:#dc2626;';
    errorDiv.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style="margin:0 auto 16px">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <p style="font-size:16px;font-weight:500;margin:0">${this.escapeHtml(message)}</p>
    `;
    
    if (this.content) {
      this.content.innerHTML = '';
      this.content.appendChild(errorDiv);
      this.showContent();
    }
    
    setTimeout(() => this.closeModal(), 3000);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });
      
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: cart,
        bubbles: true 
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }
    
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  trapFocus() {
    const container = this.modal?.querySelector('.quick-view-container');
    if (!container) return;
    
    const focusableElements = container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    container.removeEventListener('keydown', handleTabKey);
    container.addEventListener('keydown', handleTabKey);
    
    setTimeout(() => firstElement?.focus(), 100);
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quickViewModal = new QuickViewModal();
  });
} else {
  window.quickViewModal = new QuickViewModal();
}